import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IAdapter } from "@formant/core/model/IAdapter";

import { adapterStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Adapters extends Component {
    @localSettingObservable("settings-adapters-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-adapters-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;

        const columns: IDataTableColumn<IAdapter>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="adapter" />,
                sortBy: _ => _.name,
            },
            {
                header: "exec command",
                row: _ => _.execCommand,
                sortBy: _ => _.execCommand,
            },
            {
                header: "File ID",
                row: _ => _.fileId,
                sortBy: _ => _.fileId,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "lg",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "lg",
            },
            {
                header: "",
                row: _ => <Actions adapter={_} />,
            },
        ];

        const adapters = adapterStore.listAll();
        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions: {
            dimensionKey: string;
            dimensionName: string;
            getValue: (_: IAdapter) => any;
        }[] = [];

        const filtered = applySimpleFilter(
            adapters || [],
            dimensions,
            _ => _.name,
            filter,
        );

        return (
            <SettingsPage
                title="Adapters"
                extra={
                    <Help
                        text="A adapter is a logical channel of telemetry"
                        to="https://docs.formant.io/reference/real-time-sdk-guide#adapters" // TODO doc needed
                    />
                }
                actions={
                    <StandardButton to={routeTo.createAdapter()}>
                        Add adapter
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="adapter"
                    loading={!adapters}
                    totalQuantity={adapters?.length || 0}
                    totalLabel="Adapters"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={adapters || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filtered || []}
                        columns={columns}
                        loading={adapters === undefined}
                        noResultsText="No adapters found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (adapter: IAdapter) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editAdapter(
                { id: defined(adapter.id) },
                { tab: "Settings" },
            ),
        );
    };
}
